import React, { useState, useEffect } from 'react';
import { Users, BarChart2, TrendingUp, Zap, Download, BarChart3, LineChart as LineChartIcon } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import AdminCard from '../../components/admin/AdminCard';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import api from '../../services/api';
import { useProgram } from '../../contexts/ProgramContext';
import '../../styles/pages/admin/AnalyticsPage.css';
import '../../styles/admin-shared.css';

const COLORS = ['#4a90a4', '#8b5cf6', '#ec4899', '#f59e0b', '#10dc60'];

const AnalyticsPage = () => {
  // Re-fetch on program switch
  const { slug } = useProgram();
  const [platformStats, setPlatformStats] = useState(null);
  const [engagementStats, setEngagementStats] = useState(null);
  const [featuresStats, setFeaturesStats] = useState([]);
  const [contentStats, setContentStats] = useState(null);
  const [recoveryStats, setRecoveryStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchAllAnalytics = async () => {
    setLoading(true);
    try {
      const [platform, engagement, features, content, recovery] = await Promise.all([
        api.get('/admin/analytics/platform'),
        api.get('/admin/analytics/engagement'),
        api.get('/admin/analytics/features'),
        api.get('/admin/analytics/content'),
        api.get('/admin/analytics/recovery')
      ]);

      setPlatformStats(platform.data.data || {});
      setEngagementStats(engagement.data.data || {});
      setFeaturesStats(features.data.data || []);
      setContentStats(content.data.data || {});
      setRecoveryStats(recovery.data.data || {});
    } catch (error) {
      console.error('Erro ao carregar analytics:', error);
      // Set empty states on error
      setPlatformStats({});
      setEngagementStats({});
      setFeaturesStats([]);
      setContentStats({});
      setRecoveryStats({});
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/admin/analytics/export');
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { 
        type: 'application/json' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics-${new Date().toISOString()}.json`;
      a.click();
    } catch (error) {
      console.error('Erro ao exportar dados:', error);
      alert('Erro ao exportar dados');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="analytics-loading">
          <div className="spinner"></div>
          <p>Carregando analytics...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="analytics-page">
        <div className="page-header">
          <div>
            <h1><BarChart2 size={26} /> Analytics & Métricas</h1>
            <p>Visualize estatísticas e métricas da plataforma</p>
          </div>
          <button className="btn btn-outline" onClick={handleExport}>
            <Download size={16} /> Exportar Dados
          </button>
        </div>

        {/* Overview Cards */}
        <div className="analytics-cards-grid">
          <AdminCard
            title="Total de Usuários"
            value={engagementStats?.overview?.total_users?.toLocaleString() || '0'}
            subtitle={`${engagementStats?.overview?.active_users_in_period || 0} ativos no período`}
            icon={<Users size={22} />}
            color="blue"
          />
          <AdminCard
            title="Taxa de Engajamento"
            value={`${engagementStats?.overview?.engagement_rate || '0'}%`}
            subtitle="Últimos 30 dias"
            icon={<BarChart3 size={22} />}
            color="green"
          />
          <AdminCard
            title="DAU / MAU"
            value={engagementStats?.activeUsers?.dau_mau_ratio || '0'}
            subtitle="Ratio de atividade"
            icon={<TrendingUp size={22} />}
            color="purple"
          />
          <AdminCard
            title="Usuários Ativos Diários"
            value={engagementStats?.activeUsers?.daily?.toLocaleString() || '0'}
            subtitle="Últimas 24 horas"
            icon={<Zap size={22} />}
            color="yellow"
          />
        </div>

        {/* Engagement Section */}
        <div className="analytics-section">
          <h2 className="section-title">Engajamento de Usuários</h2>
          {engagementStats?.retention && engagementStats.retention.length > 0 ? (
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={engagementStats.retention}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="_count.user_id" 
                    stroke={COLORS[0]} 
                    strokeWidth={2}
                    name="Usuários Ativos"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="_avg.time_in_app" 
                    stroke={COLORS[1]} 
                    strokeWidth={2}
                    name="Tempo Médio (min)"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="empty-state">
              <LineChartIcon size={40} className="empty-icon" />
              <p>Nenhuma informação de engajamento disponível</p>
              <small>Os dados aparecerão aqui quando houver atividade de usuários</small>
            </div>
          )}
        </div>

        {/* Features Usage */}
        <div className="analytics-section">
          <h2 className="section-title">Uso de Funcionalidades</h2>
          {featuresStats && featuresStats.length > 0 ? (
            <div className="charts-row">
              <div className="chart-container half">
                <h3>Top Funcionalidades</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={featuresStats.slice(0, 10)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="feature" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="total_uses" fill={COLORS[0]} name="Total de Usos" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="chart-container half">
                <h3>Distribuição de Uso</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={featuresStats.slice(0, 5)}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) => entry.feature}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="total_uses"
                    >
                      {featuresStats.slice(0, 5).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <BarChart2 size={40} className="empty-icon" />
              <p>Nenhuma informação de uso disponível</p>
              <small>Os dados aparecerão aqui quando os usuários começarem a usar as funcionalidades</small>
            </div>
          )}
        </div>

        {/* Content Stats */}
        <div className="analytics-section">
          <h2 className="section-title">Estatísticas de Conteúdo</h2>
          <div className="stats-grid">
            <div className="stat-box">
              <div className="stat-label">Total de Posts</div>
              <div className="stat-value">{contentStats?.totals?.posts?.toLocaleString() || '0'}</div>
            </div>
            <div className="stat-box">
              <div className="stat-label">Total de Comentários</div>
              <div className="stat-value">{contentStats?.totals?.comments?.toLocaleString() || '0'}</div>
            </div>
            <div className="stat-box">
              <div className="stat-label">Total de Mensagens</div>
              <div className="stat-value">{contentStats?.totals?.messages?.toLocaleString() || '0'}</div>
            </div>
            <div className="stat-box">
              <div className="stat-label">Entradas de Diário</div>
              <div className="stat-value">{contentStats?.totals?.diary_entries?.toLocaleString() || '0'}</div>
            </div>
            <div className="stat-box">
              <div className="stat-label">Grupos Ativos</div>
              <div className="stat-value">{contentStats?.totals?.active_groups?.toLocaleString() || '0'}</div>
            </div>
            <div className="stat-box">
              <div className="stat-label">Desafios</div>
              <div className="stat-value">{contentStats?.totals?.challenges?.toLocaleString() || '0'}</div>
            </div>
          </div>
        </div>

        {/* Recovery Stats */}
        <div className="analytics-section">
          <h2 className="section-title">Estatísticas de Recuperação</h2>
          <div className="stats-grid">
            <div className="stat-box highlight">
              <div className="stat-label">Média de Dias Sóbrios</div>
              <div className="stat-value">{recoveryStats?.sober_days?.average || '0'}</div>
            </div>
            <div className="stat-box highlight">
              <div className="stat-label">Maior Sequência</div>
              <div className="stat-value">{recoveryStats?.streaks?.max || '0'} dias</div>
            </div>
            <div className="stat-box highlight">
              <div className="stat-label">Dinheiro Economizado</div>
              <div className="stat-value">
                R$ {recoveryStats?.money_saved?.total || '0.00'}
              </div>
            </div>
            <div className="stat-box highlight">
              <div className="stat-label">Taxa de Conquistas</div>
              <div className="stat-value">{recoveryStats?.achievements?.unlock_rate || '0'}%</div>
            </div>
            <div className="stat-box highlight">
              <div className="stat-label">Taxa de Conclusão de Desafios</div>
              <div className="stat-value">{recoveryStats?.challenges?.completion_rate || '0'}%</div>
            </div>
            <div className="stat-box highlight">
              <div className="stat-label">Total de Conquistas</div>
              <div className="stat-value">{recoveryStats?.achievements?.unlocked || '0'} / {recoveryStats?.achievements?.total || '0'}</div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AnalyticsPage;
